import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/authContext';
import PolicyDetails from './components/PolicyDetails';
import PolicyList from './components/PolicyList';
import PolicyTemplatesModal from './components/PolicyTemplatesModal';
import PolicyTestModal from './components/PolicyTestModal';
import { usePolicies } from './hooks/usePolicies';
import { getAdminStatusMessage, isAdmin } from './utils/adminAccess';
export default function PolicyConfigPage() {
    const { user } = useAuth();
    const { policies, loading, error, fetchPolicies, fetchPolicyDetails, testPolicy } = usePolicies();
    const userIsAdmin = isAdmin(user);
    // Debug: Log user data
    console.log('PolicyConfigPage - user:', user, 'tenant_id:', user?.tenant_id);
    const [selectedPolicy, setSelectedPolicy] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [detailsError, setDetailsError] = useState(null);
    const [showTestModal, setShowTestModal] = useState(false);
    const [showTemplatesModal, setShowTemplatesModal] = useState(false);
    const [testError, setTestError] = useState(null);
    const [testResults, setTestResults] = useState(null);
    // Debounce quick tests to prevent multiple requests in rapid succession
    const quickTestTimeoutRef = useRef(null);
    const isTestingRef = useRef(false);
    const handleSelectPolicy = async (policy)=>{
        setLoadingDetails(true);
        setDetailsError(null);
        try {
            const details = await fetchPolicyDetails(policy.id);
            setSelectedPolicy(details);
        } catch (err) {
            setDetailsError(err instanceof Error ? err.message : 'Failed to fetch policy details');
        } finally{
            setLoadingDetails(false);
        }
    };
    const runPolicyTest = useCallback(async (testRequest)=>{
        setTestError(null);
        try {
            const result = await testPolicy(testRequest);
            setTestResults({
                request: testRequest,
                response: result,
                timestamp: new Date().toISOString()
            });
        } catch (err) {
            setTestError(err instanceof Error ? err.message : 'Policy test failed');
            throw err;
        }
    }, [
        testPolicy
    ]);
    // Memoize default test request template to avoid recreating on every render
    const defaultTestRequest = useMemo(()=>({
            principal: {
                id: 'test-user',
                email: 'test@example.com',
                roles: [
                    'user'
                ],
                tenant_id: 'default',
                scopes: [
                    'read'
                ],
                attr: {
                    department: 'engineering'
                }
            },
            resource: {
                kind: 'resource',
                id: 'test-resource',
                tenant_id: 'default',
                attr: {
                    visibility: 'public',
                    owner: 'test-user'
                }
            },
            action: 'read'
        }), []);
    const quickTestPolicy = useCallback(async (policyName)=>{
        // Prevent multiple rapid requests for the same test
        if (isTestingRef.current) {
            return;
        }
        // Clear any pending debounced calls
        if (quickTestTimeoutRef.current) {
            clearTimeout(quickTestTimeoutRef.current);
        }
        // Debounce the actual test call (500ms)
        quickTestTimeoutRef.current = setTimeout(async ()=>{
            isTestingRef.current = true;
            try {
                // Build test request with memoized defaults
                const testRequest = {
                    ...defaultTestRequest,
                    resource: {
                        ...defaultTestRequest.resource,
                        kind: policyName.includes('document') ? 'document' : 'resource'
                    }
                };
                await runPolicyTest(testRequest);
            } finally{
                isTestingRef.current = false;
            }
        }, 500);
    }, [
        defaultTestRequest,
        runPolicyTest
    ]);
    useEffect(()=>{
        const abortController = new AbortController();
        // Fetch policies on mount
        void fetchPolicies(abortController.signal);
        // Cleanup function to abort the request if component unmounts or effect re-runs
        return ()=>{
            abortController.abort();
            // Clean up any pending debounced tests
            if (quickTestTimeoutRef.current) {
                clearTimeout(quickTestTimeoutRef.current);
            }
        };
    }, [
        fetchPolicies
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col h-full bg-background",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "max-w-7xl mx-auto w-full px-6 py-6",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "bg-card rounded-lg shadow-lg p-6 border-border border",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between mb-6",
                            children: [
                                /*#__PURE__*/ _jsx("h1", {
                                    className: "text-3xl font-bold text-card-foreground",
                                    children: "Policy Configuration"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex space-x-3",
                                    children: [
                                        /*#__PURE__*/ _jsx(Link, {
                                            to: "/assistant",
                                            className: "px-4 py-2 bg-muted text-muted-foreground rounded-md hover:bg-muted/80 transition-colors",
                                            children: "← Back to Assistant"
                                        }),
                                        /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            onClick: ()=>setShowTestModal(true),
                                            className: "px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors",
                                            children: "Policy Tester"
                                        }),
                                        /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            onClick: ()=>setShowTemplatesModal(true),
                                            className: "px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors",
                                            children: "Policy Templates"
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: `mb-6 rounded-md p-4 border ${userIsAdmin ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-muted border-border text-muted-foreground'}`,
                            children: /*#__PURE__*/ _jsx("div", {
                                className: "flex items-center",
                                children: /*#__PURE__*/ _jsx("span", {
                                    className: "font-medium",
                                    children: getAdminStatusMessage(user)
                                })
                            })
                        }),
                        error && /*#__PURE__*/ _jsx("div", {
                            className: "bg-destructive/10 border border-destructive/30 rounded-md p-4 mb-6",
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "text-destructive",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "font-medium",
                                        children: "Error:"
                                    }),
                                    " ",
                                    error
                                ]
                            })
                        }),
                        detailsError && /*#__PURE__*/ _jsx("div", {
                            className: "bg-destructive/10 border border-destructive/30 rounded-md p-4 mb-6",
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "text-destructive",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "font-medium",
                                        children: "Details Error:"
                                    }),
                                    " ",
                                    detailsError
                                ]
                            })
                        }),
                        testError && /*#__PURE__*/ _jsx("div", {
                            className: "bg-destructive/10 border border-destructive/30 rounded-md p-4 mb-6",
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "text-destructive",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "font-medium",
                                        children: "Test Error:"
                                    }),
                                    " ",
                                    testError
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "grid grid-cols-1 lg:grid-cols-2 gap-6",
                            children: [
                                /*#__PURE__*/ _jsx(PolicyList, {
                                    policies: policies,
                                    loading: loading,
                                    selectedPolicy: selectedPolicy,
                                    onSelectPolicy: handleSelectPolicy,
                                    onQuickTest: quickTestPolicy,
                                    onRefresh: ()=>fetchPolicies()
                                }),
                                /*#__PURE__*/ _jsx(PolicyDetails, {
                                    policy: selectedPolicy,
                                    loading: loadingDetails,
                                    onQuickTest: quickTestPolicy,
                                    onCustomTest: ()=>setShowTestModal(true)
                                })
                            ]
                        }),
                        testResults && /*#__PURE__*/ _jsxs("div", {
                            className: "mt-6 border border-border rounded-lg p-4 bg-card",
                            children: [
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-xl font-semibold mb-4 text-card-foreground",
                                    children: "Latest Test Results"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "grid grid-cols-1 lg:grid-cols-2 gap-4",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx("h3", {
                                                    className: "font-semibold text-card-foreground mb-2",
                                                    children: "Test Request"
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "bg-muted rounded-md p-3",
                                                    children: /*#__PURE__*/ _jsx("pre", {
                                                        className: "text-sm font-mono text-card-foreground whitespace-pre-wrap overflow-x-auto",
                                                        children: JSON.stringify(testResults.request, null, 2)
                                                    })
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx("h3", {
                                                    className: "font-semibold text-card-foreground mb-2",
                                                    children: "Test Response"
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: `rounded-md p-3 ${testResults.response?.allowed ? 'bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800' : 'bg-destructive/10 border border-destructive/30'}`,
                                                    children: [
                                                        /*#__PURE__*/ _jsx("div", {
                                                            className: `font-bold text-lg mb-2 ${testResults.response?.allowed ? 'text-green-700 dark:text-green-400' : 'text-destructive'}`,
                                                            children: testResults.response?.allowed ? '✅ ALLOWED' : '❌ DENIED'
                                                        }),
                                                        /*#__PURE__*/ _jsx("pre", {
                                                            className: "text-sm font-mono text-card-foreground whitespace-pre-wrap overflow-x-auto",
                                                            children: JSON.stringify(testResults.response, null, 2)
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mt-4 flex justify-between items-center",
                                    children: [
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "text-sm text-muted-foreground",
                                            children: [
                                                "Tested at: ",
                                                new Date(testResults.timestamp).toLocaleString()
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            onClick: ()=>setTestResults(null),
                                            className: "px-3 py-1 text-sm bg-muted text-muted-foreground rounded hover:bg-muted/80 transition-colors",
                                            children: "Clear Results"
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mt-6 border border-border rounded-lg p-4 bg-card",
                            children: [
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "text-xl font-semibold mb-4 text-card-foreground",
                                    children: "Policy Management"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "grid grid-cols-1 md:grid-cols-3 gap-4",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "bg-primary/10 border border-primary/30 rounded-lg p-4",
                                            children: [
                                                /*#__PURE__*/ _jsx("h3", {
                                                    className: "font-semibold text-primary mb-2",
                                                    children: "Quick Stats"
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "text-2xl font-bold text-primary",
                                                    children: policies.length
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "text-sm text-primary/80",
                                                    children: "Active Policies"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "bg-secondary/10 border border-secondary/30 rounded-lg p-4",
                                            children: [
                                                /*#__PURE__*/ _jsx("h3", {
                                                    className: "font-semibold text-secondary-foreground mb-2",
                                                    children: "Policy Templates"
                                                }),
                                                /*#__PURE__*/ _jsx("button", {
                                                    type: "button",
                                                    onClick: ()=>setShowTemplatesModal(true),
                                                    className: "text-sm bg-secondary text-secondary-foreground px-3 py-1 rounded hover:bg-secondary/90 transition-colors",
                                                    children: "View Templates"
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "text-sm text-muted-foreground mt-1",
                                                    children: "Common policy patterns"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "bg-accent/10 border border-accent/30 rounded-lg p-4",
                                            children: [
                                                /*#__PURE__*/ _jsx("h3", {
                                                    className: "font-semibold text-accent-foreground mb-2",
                                                    children: "Testing"
                                                }),
                                                /*#__PURE__*/ _jsx("button", {
                                                    type: "button",
                                                    onClick: ()=>setShowTestModal(true),
                                                    className: "text-sm bg-accent text-accent-foreground px-3 py-1 rounded hover:bg-accent/90 transition-colors",
                                                    children: "Test Policies"
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "text-sm text-muted-foreground mt-1",
                                                    children: "Validate access rules"
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            }),
            showTemplatesModal && /*#__PURE__*/ _jsx(PolicyTemplatesModal, {
                onClose: ()=>setShowTemplatesModal(false)
            }),
            showTestModal && /*#__PURE__*/ _jsx(PolicyTestModal, {
                onClose: ()=>setShowTestModal(false),
                onTest: runPolicyTest,
                userTenantId: user?.tenant_id ?? undefined
            })
        ]
    });
}
